import { BAZAAR_SEO_BABY, BZ2_EDIT_PRODUCT, EDIT_PRODUCT_SUBMIT_BUTTON } from 'mk/autogenerated/translations/EditProductPage.6fbea4eeb02dff436c082d8faf72a70e'
import { canManageBazaar, canReportProducts, isAuthenticatedAs } from 'mk/bazaar/common/userUtils';
import { isJobsCategory } from 'mk/bazaar/product/categories';
import {
    formSelector,
    ProductFormData,
    Properties,
    PRODUCT_CREATE_EDIT_FORM_NAME,
} from 'mk2/apps/bazaar/components/ProductCreateEditForm';
import { EditProduct } from 'mk2/apps/bazaar/containers/EditProduct/EditProduct';
import {
    editProductLoad,
    editProductSave,
    editProductSelectCategory,
    editProductSelectProperty,
} from 'mk2/apps/bazaar/containers/EditProduct/EditProduct.actions';
import reducer from 'mk2/apps/bazaar/containers/EditProduct/EditProduct.reducers';
import saga from 'mk2/apps/bazaar/containers/EditProduct/EditProduct.sagas';
import {
    getEditProductProductState,
    getEditProductState,
} from 'mk2/apps/bazaar/containers/EditProduct/EditProduct.selectors';
import { isMultiselectProperty, paramsToSlug } from 'mk2/apps/bazaar/helpers';
import { ProductEntity, ProductSchema, ProductStockStatus, QualityCounts } from 'mk2/apps/bazaar/schemas';
import { bazaarGridUrl } from 'mk2/apps/bazaar/urls';
import { SubmitButton } from 'mk2/components/forms/SubmitButton';
import { Btn, BtnLayout } from 'mk2/components/Btn';
import { FormPageType } from 'mk2/constants/enums';
import { AppName } from 'mk2/containers/Application/Application';
import { mkReduxFormMapDispatchToProps, mkReduxFormMapStateToProps } from 'mk2/decorators/mkReduxForm';
import { cacheLast } from 'mk2/helpers/cache';
import { cachedInitialValues } from 'mk2/helpers/form';
import { entitiesWithPhotofilePropToSuccessfulPhotoUploads } from 'mk2/helpers/photos';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import { url } from 'mk2/helpers/urls';
import FormPage, { FormPageDispatchProps, FormPageStateProps } from 'mk2/pages/FormPage';
import { PageLayout } from 'mk2/pages/Page';
import { getRequestDeviceMobile, getRequestPermissions, getRequestUser, AppState } from 'mk2/reducers';
import { LoadingState } from 'mk2/reducers/loadingState';
import { HeaderMenuItem, MenuSelection, UserEntity } from 'mk2/schemas';
import { findDenormalizedEntityByCond } from 'mk2/selectors';
import { Store } from 'mk2/store/configureStore';
import React from 'react';
import { MapStateToProps } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { getFormValues } from 'redux-form';

interface OwnProps {}

interface StateProps extends FormPageStateProps {
    // loadingState: LoadingState;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    initialValues: ProductFormData;
    currentValues: ProductFormData;
    product: ProductEntity;
    loadingState: LoadingState;
    correctedFields: string[];
    qualityCounts: QualityCounts;
    isMobile: boolean;

    // Because we have buttons in the header - we need to duplicate selecting this values in the Page :(
    properties: Properties;
    category: string;
    selectedProperty: string;
    selectedCategory: string;
}

interface DispatchProps extends FormPageDispatchProps {
    onLoad(username: string);
    onSelectProperty(property: string);
    onSelectCategory(category: string);
    onSave(productId: number, values: ProductFormData);
}

type RouteProps = RouteComponentProps<{
    category: string;
    hash: string;
    title: string;
}>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class EditProductPage extends FormPage<OwnProps & RouteProps, StateProps, DispatchProps> {
    private menuSelectionCache = cacheLast();

    public mapStateToProps(): MapStateToProps<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const {
                match: { params },
            } = ownProps;

            const slug = paramsToSlug(params);
            const substate = getEditProductState(state);
            const editState = getEditProductProductState(state, slug);

            const requestUser = getRequestUser(state);
            // Seller can be already loaded from different page - search by username
            const product = findDenormalizedEntityByCond<ProductEntity>(
                state,
                ProductSchema,
                `cacheKey-EditProductPage-${slug}-product`,
                (sel) => sel.slug === slug || sel.slugsHistory?.includes(slug),
            );

            let editPhotos: ProductFormData['photos'] = [];
            if (product && product.photos) {
                editPhotos = entitiesWithPhotofilePropToSuccessfulPhotoUploads(product.photos);
            }

            let category = product?.properties.find((p) => p[0] === 'cat')[1];
            if (category === 'import') {
                category = null;
            }

            const isJobsCat = isJobsCategory(category);
            const isWorkAbroad = category === 'work_abroad';

            return {
                ...mkReduxFormMapStateToProps(PRODUCT_CREATE_EDIT_FORM_NAME)(state),
                requestUser,
                requestUserPermissions: getRequestPermissions(state),
                initialValues:
                    product &&
                    cachedInitialValues<ProductFormData>(
                        PRODUCT_CREATE_EDIT_FORM_NAME,
                        {
                            title: product.title,
                            price: product.price,
                            pricePer: product.pricePer?.toString(),
                            photos: editPhotos,
                            description: product.description,
                            eshopUrl: product.eshopUrl || null,
                            properties: {
                                quality: [product.quality],
                                ...product.properties.reduce((acc, cur) => {
                                    if (cur[0] === 'cat') {
                                        return acc;
                                    }

                                    acc[cur[0]] = acc[cur[0]] || [];
                                    acc[cur[0]].push(cur[1]);
                                    return acc;
                                }, {}),
                            },
                            category,
                            location: isJobsCat && !isWorkAbroad ? product.countyId?.toString() : null,
                            // place: isWorkAbroad ? getPlaceIdForCountryId(product.countryId)?.toString() : null,
                            place: isJobsCat ? product.placeId?.toString() : null,
                            stock: product.stockStatus === ProductStockStatus.IN_STOCK,
                            packageSize: product.packageSize,
                        },
                        product,
                    ),
                currentValues: getFormValues(PRODUCT_CREATE_EDIT_FORM_NAME)(state) as ProductFormData,
                category: formSelector(state, 'category'),
                properties: formSelector(state, 'properties'),
                selectedProperty: substate.selectedProperty,
                selectedCategory: substate.selectedCategory,
                loadingState: editState.loadingState,
                product,
                correctedFields: editState.correctedFields,
                qualityCounts: editState.qualityCounts,
                isMobile: getRequestDeviceMobile(state),
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            ...mkReduxFormMapDispatchToProps(PRODUCT_CREATE_EDIT_FORM_NAME),
            onLoad: editProductLoad,
            onSelectProperty: editProductSelectProperty,
            onSelectCategory: editProductSelectCategory,
            onSave: editProductSave,
        };
    }

    public getMetaTitle(props: Props): string {
        return `${BZ2_EDIT_PRODUCT} | ${BAZAAR_SEO_BABY}`;
    }

    public render(props: Props): false | JSX.Element {
        const { category, properties, ...rest } = props;
        return <EditProduct {...rest} />;
    }

    public getDefaultBackPath(props: Props): string {
        return url(bazaarGridUrl, {});
    }

    public getSubmitButton(props: Props): React.ReactElement {
        return <SubmitButton label={EDIT_PRODUCT_SUBMIT_BUTTON} formName={PRODUCT_CREATE_EDIT_FORM_NAME} />;
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return this.menuSelectionCache(
            {
                activeSection: app,
                activeItem: HeaderMenuItem.BazaarAddItem,
            },
            app,
        );
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer('containers.bazaar.editProduct', reducer);
        store.injectSaga('containers.bazaar.editProduct', saga);
    }

    public getPermissionRequired(props: Props): boolean {
        return (
            canManageBazaar(props.requestUserPermissions) ||
            canReportProducts(props.requestUserPermissions) ||
            !props.product ||
            isAuthenticatedAs(props.requestUser, props.product.seller.user)
        );
    }

    public getConfirmButton(props: Props) {
        const { currentFormPage, properties, selectedProperty } = props;

        if (
            currentFormPage === FormPageType.SelectBazaarProperty &&
            !isMultiselectProperty(properties.quality && properties.quality[0], selectedProperty)
        ) {
            return null;
        } else if (currentFormPage === FormPageType.SelectBazaarCategory) {
            // Select category do not need confirm button
            return null;
        } else {
            return super.getConfirmButton(props);
        }
    }

    public getPageLayout(app: AppName, props: Props): PageLayout {
        return PageLayout.MainContentNarrow;
    }

    public getHeaderLeft(props: Props): JSX.Element {
        const { currentFormPage, onCancelFormPage, selectedCategory, onSelectCategory } = props;

        const onBack = () => {
            selectedCategory ? onSelectCategory(null) : onCancelFormPage();
        };

        return currentFormPage === FormPageType.SelectBazaarCategory ? (
            <Btn layout={BtnLayout.Icon} icon="mkicon-arrow-back@3x.png" onClick={onBack} />
        ) : (
            super.getHeaderLeft(props)
        );
    }

    public getFooter(props: Props): React.ReactNode {
        const { currentFormPage } = props;

        // Jamie & Bob:
        //   Na desktope je pod fieldom "Cena" svetlejšia fullwidth čiara namiesto spodnej inputovej.
        //   Dajme ju preč, nechajme len inputovú.
        //   Zduplikujme button dole aj na mobile, rovnako ako na desktope.
        // So: This page has custom bottom submit button directly in <ProductCreateEditForm />
        if (currentFormPage === FormPageType.Main) {
            return null;
        } else {
            super.getFooter(props);
        }
    }
}
